import { VideoFormState, PromptPreset } from '../types/heygen';
import type { HeyGenPayload, HeyGenDimension } from '../../../types/avatar-video';
import { getPhotoId, resolutionToAspectRatio } from './avatar-lookups';
import { type Plano } from '../constants/avatar-matrix';
/**
 * Construye el payload que espera la API de HeyGen a partir del estado del form.
 * Los campos fijos (use_avatar_iv_model, matting, speed, locale, dimension) se
 * hardcodean acá y no son responsabilidad del form.
 *
 * Resuelve el talking_photo_id desde la avatar matrix usando vestuario + ambiente + resolución + plano.
 * Background fijo: color blanco (los avatars tienen fondo integrado).
 */
export const DIMENSION_MAP: Record<string, HeyGenDimension> = {
  fullhd: { width: 1920, height: 1080 },
  vertical: { width: 1080, height: 1920 }
};

/**
 * Reverse lookup del `selectedResolution` a partir del dimension del proyecto.
 * Se usa al entrar en edit mode desde el historial: el form-data devuelve el
 * `dimension` guardado en DDB y necesitamos derivar el id de resolución que
 * usa la UI ('fullhd', 'vertical') para restaurar `selectedResolution` y que
 * `getPhotoId` resuelva el talking_photo_id correcto.
 */
export const dimensionToResolutionId = (dimension: HeyGenDimension): string => {
  if (dimension.width === 1920 && dimension.height === 1080) return 'fullhd';
  if (dimension.width === 1080 && dimension.height === 1920) return 'vertical';
  return 'fullhd';
};

export const buildPayload = (
  state: VideoFormState,
  presets: PromptPreset[],
  dimension: HeyGenDimension = DIMENSION_MAP.fullhd,
  selectedResolution: string = 'fullhd'
): HeyGenPayload => {
  const preset = presets.find(p => p.key === state.promptKey);
  const prompt = preset?.prompt ?? state.promptKey;
  const resolucion = resolutionToAspectRatio(selectedResolution);

  return {
    title: state.title,
    video_inputs: state.scenes.map(scene => {
      // Escena solo voz: el payload no lleva character/background.
      if (scene.onlyVoice) {
        return {
          script: scene.script,
          onlyVoice: true as const
        };
      }

      const photoId = getPhotoId(scene.vestuario, scene.ambiente, resolucion, scene.plano as Plano);
      if (!photoId) {
        throw new Error(`Avatar no encontrado: ${scene.vestuario}|${scene.ambiente}|${resolucion}|${scene.plano}`);
      }
      return {
        character: {
          type: 'talking_photo' as const,
          talking_photo_id: photoId,
          use_avatar_iv_model: true as const,
          prompt,
          talking_style: state.talkingStyle,
          matting: false
        },
        background: {
          type: 'color' as const,
          value: '#FFFFFF'
        },
        script: scene.script,
        onlyVoice: false as const
      };
    }),
    dimension
  };
};
